import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TFunction, useTranslation } from 'react-i18next';
import { Button, Modal, Spin, Table, TablePaginationConfig } from 'antd';
import OmniNotification from 'components/OmniNotification';
import { AppDispatch } from 'redux/store';
import { isNil } from 'lodash';
import { getAPISorter, getCellValue, getShowTotal } from 'utils/tableUtils';
import { getFormattedDate, getFormattedDateTime } from 'utils/dateUtils';
import styles from './RedemptionCodesTab.module.less';
import {
	CollaboratorPermission,
	ProductStatus,
	ProjectStatusDto,
	RedemptionCodeDto,
	RedemptionCodeListSortField,
} from 'api';
import {
	getRedemptionCodes,
	deleteRedemptionCodes,
} from 'redux/reducers/redemptionCodeSlice';
import { DeleteOutlined } from '@ant-design/icons';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import { userSelector } from 'redux/selectors/userSelector';
import { getProjectStatus } from 'redux/reducers/projectsSlice';

type SorterType =
	| SorterResult<RedemptionCodeDto>
	| SorterResult<RedemptionCodeDto>[];

const DefaultSorter = {
	order: 'descend' as SortOrder,
	column: {
		APISortField: RedemptionCodeListSortField.CreatedAt,
	} as any,
};

const getColumns = (
	t: TFunction,
	sorterRef: SorterResult<RedemptionCodeDto>,
	deleteModalOpen: Function,
) => [
	{
		title: t('redemptionCodesPage.Table.Code'),
		key: 'code',
		render: (record: RedemptionCodeDto) => getCellValue(record.code),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, RedemptionCodeListSortField.Code),
		APISortField: RedemptionCodeListSortField.Code,
	},
	{
		title: t('redemptionCodesPage.Table.Created'),
		key: 'createdDate',
		render: (record: RedemptionCodeDto) =>
			getCellValue(getFormattedDateTime(record.createdDate)),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, RedemptionCodeListSortField.CreatedAt),
		APISortField: RedemptionCodeListSortField.CreatedAt,
	},
	{
		title: t('redemptionCodesPage.Table.CreatedBy'),
		key: 'createBy',
		render: (record: RedemptionCodeDto) => getCellValue(record.createdBy),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, RedemptionCodeListSortField.CreatedBy),
		APISortField: RedemptionCodeListSortField.CreatedBy,
	},
	{
		title: t('redemptionCodesPage.Table.Redeemed'),
		key: 'userId',
		render: (record: RedemptionCodeDto) =>
			getCellValue(getFormattedDateTime(record.lastModifiedDate)),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, RedemptionCodeListSortField.RedeemedAt),
		APISortField: RedemptionCodeListSortField.RedeemedAt,
	},
	{
		title: t('redemptionCodesPage.Table.Player'),
		key: 'userName',
		render: (record: RedemptionCodeDto) => (
			<span>{getCellValue(record.userName)}</span>
		),
		sorter: true,
		sortOrder: getSortOrder(sorterRef, RedemptionCodeListSortField.UserName),
		APISortField: RedemptionCodeListSortField.UserName,
	},
	{
		title: t('redemptionCodesPage.Table.Delete'),
		key: 'delete',
		render: (record: RedemptionCodeDto) => (
			<Button
				disabled={!!record.userId}
				type="text"
				icon={<DeleteOutlined />}
				onClick={() => deleteModalOpen(record)}
			/>
		),
		width: 80,
		align: 'center' as const,
	},
];

const getSortOrder = (
	sorterRef: SorterResult<RedemptionCodeDto>,
	field: RedemptionCodeListSortField,
) => {
	return (sorterRef?.column as any)?.APISortField === field
		? sorterRef.order
		: undefined;
};

interface Props {
	setSelectedRowsCallBackFunction: (param: RedemptionCodeDto[]) => void;
	setHasNoRedemptionCodeBackFunction: (param: boolean) => void;
	resetAllowRefetchData: () => void;
	allowRefetchData: boolean;
	setSorterRefForExporting: (sorter: SorterType) => void;
	isActiveTab: boolean;
	projectId: number;
}

export default function OmniOnlineTab(props: Props) {
	const {
		setSelectedRowsCallBackFunction,
		resetAllowRefetchData,
		allowRefetchData,
		setSorterRefForExporting,
		setHasNoRedemptionCodeBackFunction,
		isActiveTab,
		projectId,
	} = props;
	const dispatch: AppDispatch = useDispatch();
	const { t } = useTranslation();
	const showDeleteConfirmation = (redemptionCode: RedemptionCodeDto) =>
		deleteModal([redemptionCode]);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});
	const [sorterRef, setSorterRef] = useState<SorterType>(DefaultSorter);
	const columns = useMemo(
		() =>
			getColumns(
				t,
				sorterRef as SorterResult<RedemptionCodeDto>,
				showDeleteConfirmation,
			),
		[t, sorterRef, showDeleteConfirmation],
	);

	const [tableData, setTableData] = useState<RedemptionCodeDto[]>([]);
	const [hasNoRedemptionCode, setHasNoRedemptionCode] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [tableIsLoading, setTableIsLoading] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

	const userData = useSelector(userSelector);
	const projectPermissions = userData?.projectPermissions?.find(
		(x: any) => x.projectId === projectId,
	)?.permissions;
	const [projectStatus, setProjectStatus] = useState<ProjectStatusDto>();

	const refetchData = async () => {
		setPagination({ ...pagination, current: 1 });
		await handleTableChange({ ...pagination, current: 1 }, null, DefaultSorter);
		resetAllowRefetchData();
	};

	const initProjectStatus = async () => {
		setIsLoading(true);
		const result = await dispatch(getProjectStatus(projectId));
		setIsLoading(false);
		if (getProjectStatus.fulfilled.match(result)) {
			setProjectStatus(result.payload);
		}
	};

	useEffect(() => {
		if (allowRefetchData) {
			refetchData();
		}
	}, [allowRefetchData]);

	useEffect(() => {
		const getChanges = async () => {
			setIsLoading(true);
			await handleTableChange(
				{ ...pagination, current: 1 },
				null,
				DefaultSorter,
			);
			await initProjectStatus();
			setIsLoading(false);
		};
		getChanges();
	}, []);

	useEffect(() => {
		setHasNoRedemptionCodeBackFunction(isActiveTab && !tableData.length);
	}, [isActiveTab, tableData]);

	useEffect(() => {
		if (!isActiveTab) {
			setSelectedRowKeys([]);
			setSelectedRowsCallBackFunction([]);
		}
	}, [isActiveTab]);

	const handleTableChange = async (
		tablePagination: TablePaginationConfig,
		filters: any,
		sorter: SorterType,
	) => {
		setTableIsLoading(true);
		setSorterRef(sorter);
		setSorterRefForExporting(sorter);
		const result = await dispatch(
			getRedemptionCodes({
				projectId,
				pageNumber: tablePagination.current || 1,
				pageSize: tablePagination.pageSize as number,
				...getAPISorter(sorter),
			}),
		);
		setTableIsLoading(false);

		if (getRedemptionCodes.fulfilled.match(result)) {
			setTableData(result.payload.items || []);
			setPagination({
				...pagination,
				...tablePagination,
				total: result.payload.totalCount || 0,
			});
		}

		setHasNoRedemptionCode(
			!!(!(result.payload as any)?.totalCount && tablePagination.current),
		);
	};

	const deleteModal = (redemptionCodes: RedemptionCodeDto[]) => {
		Modal.confirm({
			title: (
				<span className={styles.warningModalHeader}>
					{t('redemptionCodesPage.DeleteRedemptionCodeModal.Header')}
				</span>
			),
			okText: t('redemptionCodesPage.DeleteRedemptionCodeModal.OkButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleBatchDeleteRedemptionCode(redemptionCodes);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('redemptionCodesPage.DeleteRedemptionCodeModal.Body', {
						num: redemptionCodes.length,
					})}
				</span>
			),
		});
	};

	const handleBatchDeleteRedemptionCode = async (
		redemptionCodes: RedemptionCodeDto[],
	) => {
		if (!redemptionCodes || redemptionCodes.length <= 0) return;
		const ids = redemptionCodes
			.filter((x) => !!x.id)
			.map((x) => x.id as number);
		try {
			await dispatch(deleteRedemptionCodes({ projectId, ids }));
			OmniNotification.success({
				message: t(
					'redemptionCodesPage.DeleteRedemptionCodeModal.Success.Header',
				),
				description: t(
					'redemptionCodesPage.DeleteRedemptionCodeModal.Success.Body',
					{ count: ids.length },
				),
			});
			setSelectedRowsCallBackFunction([]);
		} catch (e) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		} finally {
			await handleTableChange(pagination, null, DefaultSorter);
		}
	};

	const rowSelection = {
		onChange: (selectedKeys: React.Key[], rows: RedemptionCodeDto[]) => {
			setSelectedRowsCallBackFunction(rows);
			setSelectedRowKeys(selectedKeys);
		},
		getCheckboxProps: (record: RedemptionCodeDto) => {
			return {
				disabled: !!record.userId,
			};
		},
	};

	const renderTable = () => {
		if (
			projectStatus?.status == ProductStatus.Released &&
			!hasNoRedemptionCode
		) {
			return (
				<Table
					className="table"
					size="middle"
					columns={columns}
					rowKey={(record) => record.id || ('' as string)}
					dataSource={tableData}
					pagination={pagination}
					loading={tableIsLoading}
					onChange={handleTableChange}
					tableLayout="fixed"
					sortDirections={['descend', 'ascend', 'descend']}
					data-testid="redemptionCodesTable"
					rowSelection={{
						type: 'checkbox',
						...rowSelection,
					}}
				/>
			);
		} else if (projectStatus?.status != ProductStatus.Released) {
			if (
				projectPermissions?.includes(CollaboratorPermission.RedemptionCodes)
			) {
				return (
					<div className={styles.emptyList} data-testid="notReadyDiv">
						<h2 className="h3">{t('redemptionCodesPage.NotReady.Line1')}</h2>
						<br />
						<h2 className="h3">{t('redemptionCodesPage.NotReady.Line2')}</h2>
					</div>
				);
			} else {
				return (
					<div className={styles.emptyList} data-testid="noAccessDiv">
						<h2 className="h3">{t('redemptionCodesPage.NoAccess.Line1')}</h2>
						<br />
						<br />
						<ol>
							<li className="h3">{t('redemptionCodesPage.NoAccess.Line2')}</li>
							<li className="h3">{t('redemptionCodesPage.NoAccess.Line3')}</li>
						</ol>
					</div>
				);
			}
		} else if (hasNoRedemptionCode) {
			return (
				<div className={styles.emptyList} data-testid="noCodesDiv">
					<h2 className="h3">{t('redemptionCodesPage.None.Line1')}</h2>
					<br />
					<h2 className="h3">{t('redemptionCodesPage.None.Line2')}</h2>
				</div>
			);
		}
	};

	if (isLoading) return <Spin />;

	return <div>{isLoading ? <Spin /> : renderTable()}</div>;
}
